import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Download, Loader2, AlertCircle } from "lucide-react";

interface LeadMagnetFormProps {
  /** Funnel attribution string persisted on each lead (e.g. "positioning-playbook"). */
  source: string;
  ctaLabel?: string;
  /** Centered alignment for full-width sections; left-aligned reads better in copy-heavy heroes. */
  align?: "left" | "center";
  testIdPrefix?: string;
}

/**
 * Email-only lead capture form. POSTs to `/api/newsletter/lead` and renders an inline
 * confirmation on success. The server saves the lead and sends the playbook link via
 * SendGrid or Microsoft Graph (`sendPlaybookLeadEmail` in `server/email.ts`).
 */
export function LeadMagnetForm({
  source,
  ctaLabel = "Subscribe to our Tech Retriever newsletter to receive the Playbook",
  align = "left",
  testIdPrefix = "leadform",
}: LeadMagnetFormProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  /** Honeypot for bots — hidden from users, must stay empty. */
  const [website, setWebsite] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/newsletter/lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim().toLowerCase(),
          source,
          website,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Something went wrong. Please try again.");
      }
      return res.json();
    },
    onSuccess: () => setSubmitted(true),
  });

  const wrapperAlign = align === "center" ? "mx-auto text-center" : "";

  if (submitted) {
    return (
      <div
        className={`rounded-2xl border border-primary/30 bg-primary/5 p-6 max-w-xl ${wrapperAlign}`}
        role="status"
        aria-live="polite"
        data-testid={`${testIdPrefix}-success`}
      >
        <div className={`flex items-start gap-3 ${align === "center" ? "justify-center text-left" : ""}`}>
          <CheckCircle2 className="text-primary mt-0.5 shrink-0" size={20} aria-hidden="true" />
          <div>
            <p className="font-semibold mb-1">You're in. Check your inbox.</p>
            <p className="text-sm text-foreground/70">
              The SaaS Positioning Playbook is on its way to <span className="text-foreground font-medium">{email}</span>. If it doesn't land in 5 minutes, peek in spam or promotions.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!mutation.isPending) mutation.mutate();
      }}
      className={`max-w-xl ${wrapperAlign}`}
      noValidate
      data-testid={`${testIdPrefix}-form`}
    >
      <div
        aria-hidden="true"
        className="absolute h-0 w-0 overflow-hidden opacity-0 pointer-events-none"
        style={{ left: "-9999px" }}
      >
        <Label htmlFor={`${testIdPrefix}-website`}>Website</Label>
        <input
          id={`${testIdPrefix}-website`}
          name="website"
          type="text"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label htmlFor={`${testIdPrefix}-name`} className="sr-only">
            Your name
          </Label>
          <Input
            id={`${testIdPrefix}-name`}
            name="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            required
            minLength={1}
            maxLength={120}
            autoComplete="name"
            className="bg-card border-white/10 h-12"
            data-testid={`${testIdPrefix}-input-name`}
          />
        </div>
        <div>
          <Label htmlFor={`${testIdPrefix}-email`} className="sr-only">
            Work email
          </Label>
          <Input
            id={`${testIdPrefix}-email`}
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Work email"
            required
            maxLength={255}
            autoComplete="email"
            className="bg-card border-white/10 h-12"
            data-testid={`${testIdPrefix}-input-email`}
          />
        </div>
      </div>
      <Button
        type="submit"
        disabled={mutation.isPending}
        size="lg"
        className="w-full mt-3 h-12 font-semibold"
        data-testid={`${testIdPrefix}-submit`}
      >
        {mutation.isPending ? (
          <>
            <Loader2 size={16} className="mr-2 animate-spin" aria-hidden="true" /> Sending…
          </>
        ) : (
          <>
            <Download size={16} className="mr-2" aria-hidden="true" /> {ctaLabel}
          </>
        )}
      </Button>
      {mutation.isError && (
        <div
          className="mt-3 flex items-start gap-2 text-sm text-red-400"
          role="alert"
          data-testid={`${testIdPrefix}-error`}
        >
          <AlertCircle size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
          <p>{(mutation.error as Error).message}</p>
        </div>
      )}
      <p className="mt-3 text-xs text-muted-foreground">
        We respect your inbox. Our emails go out every other week, no spam, unsubscribe in one click.
      </p>
    </form>
  );
}
